import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowRight, Download } from "lucide-react";

/**
 * Compact "Recently updated documents" widget for the public Home page.
 * Renders nothing when there are no active docs.
 */
export default function RecentAcademicsWidget({
  docs = [],
  theme = "amber",
  testidPrefix = "ac",
  linkTo = "academics",
  limit = 3,
  heading = "Recently Updated",
  eyebrow = "Academic Resources",
}) {
  const THEMES = {
    amber: { accent: "#D48F1A", border: "border-amber-200", soft: "bg-amber-50" },
    navy: { accent: "#D4A017", border: "border-slate-200", soft: "bg-[#001F3F]/5" },
    cream: { accent: "#AF734A", border: "border-[#E5D9C6]", soft: "bg-[#FBF6EE]" },
    saffron: { accent: "#FF7B00", border: "border-[#F0E2C0]", soft: "bg-[#FFF1DC]" },
    cyan: { accent: "#06B6D4", border: "border-cyan-200", soft: "bg-cyan-50" },
    emerald: { accent: "#0FB28A", border: "border-emerald-200", soft: "bg-emerald-50" },
    yellow: { accent: "#FDC700", border: "border-yellow-200", soft: "bg-yellow-50" },
    orange: { accent: "#F4751F", border: "border-orange-200", soft: "bg-orange-50" },
  };
  const t = THEMES[theme] || THEMES.amber;

  const recent = useMemo(() => {
    const active = (docs || []).filter((d) => (d.status || "Active") === "Active" && d.pdf_url);
    // Sort by updated_at desc (fallback to created_at)
    active.sort((a, b) => {
      const aT = a.updated_at || a.created_at || "";
      const bT = b.updated_at || b.created_at || "";
      return bT.localeCompare(aT);
    });
    return active.slice(0, limit);
  }, [docs, limit]);

  if (recent.length === 0) return null;

  return (
    <section className="py-12" data-testid={`${testidPrefix}-recent-section`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase font-semibold" style={{ color: t.accent }}>
              {eyebrow}
            </div>
            <h2 className="mt-1 font-serif text-2xl md:text-3xl text-slate-900 tracking-tight">{heading}</h2>
          </div>
          <Link
            to={linkTo}
            data-testid={`${testidPrefix}-recent-see-all`}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.15em] hover:underline"
            style={{ color: t.accent }}
          >
            See All <ArrowRight size={12} strokeWidth={2} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {recent.map((d) => (
            <a
              key={d.id}
              href={d.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              download
              data-testid={`${testidPrefix}-recent-${d.id}`}
              className={`group bg-white border ${t.border} rounded-lg p-5 flex items-start gap-3 hover:shadow-md transition-all`}
            >
              <div
                className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0"
                style={{ background: `${t.accent}22`, color: t.accent }}
              >
                <FileText size={18} strokeWidth={1.5} />
              </div>
              <div className="min-w-0 flex-1">
                {d.academic_year && (
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: t.accent }}>
                    AY {d.academic_year}
                  </div>
                )}
                <div className="mt-0.5 font-medium text-slate-900 leading-snug line-clamp-2">{d.name}</div>
                <div className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-500 group-hover:text-slate-900">
                  <Download size={11} strokeWidth={2} /> Download PDF
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
